import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import {useEffect,useRef } from "react";
import HomePage from "./pages/HomePage";
import HelpPage from "./pages/HelpPage";
import NewAnalysisPage from "./pages/NewAnalysisPage";
import NewProjectPage from "./pages/NewProjectPage";
import ReferenceFramePage from "./pages/ReferenceFramePage";
import GlobalStyles from "./globalStyle";
import AnalysisResultPage from "./pages/AnalysisResultPage";
import ProjectsPage from "./pages/ProjectsPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import { useUserDataService } from "services/useUserDataService";
import Loader from "components/MainComponents/Loader/Loader";
import RootLayout from "Layouts/RootLayout";
import PublicOnlyRoute from "./Layouts/PublicOnlyRoute";
import PageNotFound from "pages/PageNotFound";
import axios from 'axios';
import { useToast } from "@coreai/component-library";
import { useLanguage } from "./LanguageContext";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import SignInPage from "pages/SignInPage";
import SuccessLogoutPage from "pages/SuccessLogoutPage";
import ResetPasswordPage from "pages/ResetPasswordPage";

function App() {
  const { userData, loading } = useUserDataService();
  const { t } = useLanguage();
  const { toast } = useToast();
  const hasShownNetworkError = useRef(false);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === 'ERR_NETWORK') {
          if (!hasShownNetworkError.current) {
            hasShownNetworkError.current = true;

            toast({
              variant: "error",
              description: t("network_error_msg"),
            });

            setTimeout(() => {
              hasShownNetworkError.current = false;
            }, 5000);
          }
        } else if (error.response?.status === 401) {
          return Promise.resolve(error);
        } else if (error.response?.status === 422) {
          toast({
              variant: "error",
              description: t(error?.response?.data?.message_key),
          });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [t, toast]);
  
  const hasRun = useRef(false);
  useEffect(() => {
    const matomoId = process.env.REACT_APP_MATOMO_ID; // for local qc link 
    const src = (!matomoId) ? 'https://qc.antigreenwashing.marcel.ai/metrics/js/container_YSwbhDto.js' : `/metrics/js/container_${matomoId}.js`; 
    if (hasRun.current) return;
    const script = document.createElement("script");
    script.src = src
    script.async = true;
    hasRun.current = true;
    document.head.appendChild(script);
  }, []);
  if (loading || !userData.token) return <Loader></Loader>;

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
      <Route path="/signin" element={
        <PublicOnlyRoute isAuthenticated={!!userData?.token?.trim()}>
          <SignInPage />
        </PublicOnlyRoute>
      } />
      <Route path="/changepassword" element={
        <PublicOnlyRoute isAuthenticated={userData?.is_llid_user}>
          <ChangePasswordPage />
        </PublicOnlyRoute>
      } />
      <Route path="/password" element={
        <PublicOnlyRoute isAuthenticated={!!userData?.token?.trim()}>
          <ResetPasswordPage />
        </PublicOnlyRoute>
      } />
      <Route path="/success" element={
        <PublicOnlyRoute isAuthenticated={!!userData?.token?.trim()}>
        <SuccessLogoutPage />
      </PublicOnlyRoute>
      } />

      <Route path="/" element={<RootLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/analyses" element={<HomePage />} />
        <Route path="/analyses/:id?" element={<HomePage />} />
        <Route path="/analyses/new" element={<NewAnalysisPage />} />
        <Route path="/analyses/results" element={<AnalysisResultPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/new" element={<NewProjectPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/rulesets" element={<ReferenceFramePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </>
    )
  );

  return (
    <>
      <GlobalStyles />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
